import api from './client'
import type {
  DeploymentHistoryItem,
  DeploymentDetail,
  DeploymentLogEntry,
  PreDeployCheckResult,
  DeployableObject,
  DbStructurePreview,
} from '@/types'

export async function listDeployableObjects(
  projectId: string
): Promise<DeployableObject[]> {
  const { data } = await api.get('/deploy/objects', {
    params: { project_id: projectId },
  })
  return data.objects
}

export async function preDeployCheck(
  projectId: string,
  objName: string,
  profileId: number,
  objType: string = 'T'
): Promise<PreDeployCheckResult> {
  const { data } = await api.post('/deploy/pre-check', {
    project_id: projectId,
    obj_name: objName,
    profile_id: profileId,
    obj_type: objType,
  })
  return data.checks
}

export async function dbStructurePreview(
  projectId: string,
  objName: string,
  profileId: number,
  objType: string = 'T'
): Promise<DbStructurePreview> {
  const { data } = await api.post('/deploy/db-structure-preview', {
    project_id: projectId,
    obj_name: objName,
    profile_id: profileId,
    obj_type: objType,
  })
  return data.preview
}

export async function executeDeploy(params: {
  project_id: string
  obj_name: string
  profile_id: number
  version_tag?: string
  commit_message?: string
  obj_type?: string
  skip_db_structure?: boolean
}): Promise<DeploymentHistoryItem> {
  const { data } = await api.post('/deploy/execute', params)
  return data.deployment
}

export async function deploySelected(params: {
  project_id: string
  profile_id: number
  selected: { obj_name: string; obj_type: string }[]
  version_tag?: string
  commit_message?: string
  skip_db_structure?: boolean
}): Promise<DeploymentHistoryItem[]> {
  const { data } = await api.post('/deploy/deploy-selected', params)
  return data.deployments
}

export async function rollbackDeployment(
  deploymentId: number
): Promise<DeploymentHistoryItem> {
  const { data } = await api.post(`/deploy/${deploymentId}/rollback`)
  return data.deployment
}

export async function getDeploymentHistory(
  projectId?: string,
  limit?: number
): Promise<DeploymentHistoryItem[]> {
  const params: Record<string, string | number> = {}
  if (projectId) params.project_id = projectId
  if (limit) params.limit = limit
  const { data } = await api.get('/deploy/history', { params })
  return data.deployments
}

export async function getDeploymentDetail(
  deploymentId: number
): Promise<DeploymentDetail> {
  const { data } = await api.get(`/deploy/${deploymentId}`)
  return data.deployment
}

export async function getDeploymentLogs(
  deploymentId: number
): Promise<DeploymentLogEntry[]> {
  const { data } = await api.get(`/deploy/${deploymentId}/logs`)
  return data.logs
}

export interface UncommittedChange {
  file_path: string
  status: string
  diff: string
}

export async function getUncommittedChanges(
  projectId: string
): Promise<UncommittedChange[]> {
  const { data } = await api.get('/deploy/uncommitted-changes', {
    params: { project_id: projectId },
  })
  return data.changes
}

export async function getNextVersion(
  projectId: string,
  objName: string,
  objType: string = 'T'
): Promise<string> {
  const { data } = await api.get('/deploy/next-version', {
    params: { project_id: projectId, obj_name: objName, obj_type: objType },
  })
  return data.version
}
